import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Camera, ArrowsClockwise, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Native camera capture using getUserMedia. Works on mobile + desktop.
 * Calls onCapture(file: File) with the captured photo as a JPEG File.
 */
export default function CameraCapture({ open, onOpenChange, label, onCapture }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null); // dataURL
  const [facing, setFacing] = useState("environment"); // 'environment' | 'user'

  // Start / stop camera
  useEffect(() => {
    if (!open) {
      stopStream();
      setPreview(null);
      setError(null);
      setReady(false);
      return;
    }
    startStream(facing);
    return () => stopStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, facing]);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startStream = async (mode) => {
    setError(null);
    setReady(false);
    stopStream();
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Tu navegador no soporta acceso a la cámara.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: mode }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setReady(true);
    } catch (e) {
      // Try without facingMode constraint as fallback (desktop)
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch (e2) {
        setError(
          "No se pudo acceder a la cámara. Asegúrate de dar permiso en tu navegador."
        );
      }
    }
  };

  const snap = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    const w = v.videoWidth || 1280;
    const h = v.videoHeight || 720;
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0, w, h);
    const data = c.toDataURL("image/jpeg", 0.9);
    setPreview(data);
    stopStream();
  };

  const retake = () => {
    setPreview(null);
    startStream(facing);
  };

  const flip = () => {
    setFacing((f) => (f === "environment" ? "user" : "environment"));
  };

  const accept = async () => {
    if (!preview) return;
    try {
      const resp = await fetch(preview);
      const blob = await resp.blob();
      const file = new File([blob], `foto_${Date.now()}.jpg`, { type: "image/jpeg" });
      onCapture && onCapture(file);
      onOpenChange(false);
    } catch (e) {
      toast.error("No se pudo guardar la foto");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-none border-slate-900 max-w-3xl p-0 gap-0 bg-black"
        data-testid="camera-capture-dialog"
      >
        <DialogHeader className="px-4 py-3 border-b border-slate-700 bg-slate-900 text-white space-y-1">
          <DialogTitle className="font-display text-base flex items-center gap-2">
            <Camera size={16} weight="bold" /> Tomar foto · {label}
          </DialogTitle>
          <DialogDescription className="text-slate-300 text-xs">
            Encuadra el documento y captura. Puedes repetir o cambiar de cámara.
          </DialogDescription>
        </DialogHeader>

        <div className="relative w-full bg-black aspect-[4/3] sm:aspect-[16/9] flex items-center justify-center">
          {!preview ? (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-contain"
                data-testid="camera-video"
              />
              {!ready && !error && (
                <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
                  Iniciando cámara...
                </div>
              )}
              {error && (
                <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
                  <div className="bg-white text-slate-900 p-4 max-w-sm">
                    <div className="font-display font-bold mb-1">Sin acceso a la cámara</div>
                    <p className="text-xs">{error}</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <img
              src={preview}
              alt="Captura"
              className="w-full h-full object-contain"
              data-testid="camera-preview"
            />
          )}
          <canvas ref={canvasRef} className="hidden" />
        </div>

        <div className="px-4 py-3 bg-slate-900 flex items-center justify-between gap-2">
          {!preview ? (
            <>
              <button
                onClick={() => onOpenChange(false)}
                className="px-3 py-2 text-white text-xs font-bold uppercase tracking-wider hover:bg-slate-800"
                data-testid="camera-cancel-button"
              >
                Cancelar
              </button>
              <button
                onClick={snap}
                disabled={!ready}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-white text-slate-900 text-xs font-bold uppercase tracking-wider hover:bg-[#6B21A8] hover:text-white transition-colors disabled:opacity-40"
                data-testid="camera-snap-button"
              >
                <Camera size={14} weight="bold" /> Capturar
              </button>
              <button
                onClick={flip}
                className="inline-flex items-center gap-1 px-3 py-2 text-white text-xs font-bold uppercase tracking-wider hover:bg-slate-800"
                title="Cambiar cámara"
                data-testid="camera-flip-button"
              >
                <ArrowsClockwise size={14} weight="bold" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={retake}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-white/50 text-white text-xs font-bold uppercase tracking-wider hover:bg-slate-800"
                data-testid="camera-retake-button"
              >
                <X size={13} weight="bold" /> Repetir
              </button>
              <button
                onClick={accept}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#6B21A8] hover:bg-[#581C87] text-white text-xs font-bold uppercase tracking-wider"
                data-testid="camera-accept-button"
              >
                <Check size={14} weight="bold" /> Usar foto
              </button>
              <div className="w-9" />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
